import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Form, 
  Input, 
  Button, 
  Row, 
  Col, 
  Select, 
  Space,
  Divider,
  Typography,
  Upload,
  Avatar
} from 'antd';
import { SaveOutlined, EditOutlined, CameraOutlined, BuildOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { companyProfileQueryKeys } from '../../../lib/queryClient';
import { UpdateCompanyProfileRequest } from '../../../types';

const { Title, Text } = Typography;
const { TextArea } = Input;

const CompanyProfileTab: React.FC = () => {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [editing, setEditing] = useState(false);

  const { data: profileData } = useQuery({
    queryKey: companyProfileQueryKeys.profile,
    queryFn: async () => {
      const response = await apiService.getCompanyProfile();
      if (!response.success) throw new Error(response.message);
      return response.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const updateMutation = useMutation({
    mutationFn: (values: UpdateCompanyProfileRequest) => apiService.updateCompanyProfile(values),
    onSuccess: (response) => {
      if (response.success && response.data) {
        queryClient.setQueryData(companyProfileQueryKeys.profile, response.data);
        setEditing(false);
        toast.success('Profil başarıyla güncellendi.');
      } else {
        toast.error(response.message || 'Profil güncellenirken hata oluştu.');
      }
    },
    onError: () => toast.error('Profil güncellenirken hata oluştu.'),
  });

  const logoMutation = useMutation({
    mutationFn: (file: File) => apiService.uploadCompanyLogo(file),
    onSuccess: (response) => {
      if (response.success) {
        toast.success('Logo başarıyla güncellendi!');
        queryClient.invalidateQueries({ queryKey: companyProfileQueryKeys.profile });
      } else {
        toast.error(response.message || 'Logo yüklenirken hata oluştu.');
      }
    },
    onError: () => toast.error('Logo yüklenirken hata oluştu.'),
  });

  useEffect(() => {
    if (profileData) {
      form.setFieldsValue(profileData);
    }
  }, [profileData, form]);

  const companySizeOptions = [
    { value: '1-10', label: '1-10 Çalışan' },
    { value: '11-50', label: '11-50 Çalışan' },
    { value: '51-200', label: '51-200 Çalışan' },
    { value: '201-500', label: '201-500 Çalışan' },
    { value: '500+', label: '500+ Çalışan' }
  ];

  const handleSave = (values: UpdateCompanyProfileRequest) => {
    updateMutation.mutate(values);
  };

  const handleEdit = () => setEditing(true);

  const handleCancel = () => {
    setEditing(false);
    if (profileData) form.setFieldsValue(profileData);
  };

  const handleLogoUpload = (file: File) => {
    if (file.size > 10 * 1024 * 1024) {
      toast.error('Dosya boyutu 10MB\'dan büyük olamaz.');
      return false;
    }
    logoMutation.mutate(file);
    return false;
  };

  return (
    <div className="panel-tab-content">
      <Card 
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Title level={4} style={{ margin: 0 }}>Şirket Profili</Title>
            {!editing && (
              <Button icon={<EditOutlined />} onClick={handleEdit}>
                Düzenle
              </Button>
            )}
          </div>
        }
      >
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSave}
          >
          {/* Logo Upload Section */}
          <div style={{ marginBottom: '32px', textAlign: 'center' }}>
            <Text strong style={{ display: 'block', marginBottom: '16px', fontSize: '16px', color: 'var(--text-primary)' }}>
              Şirket Logosu
            </Text>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '16px' }}>
              <Avatar 
                size={120} 
                style={{ 
                  backgroundColor: 'var(--primary-100)', 
                  color: 'var(--primary-600)',
                  fontSize: '36px',
                  fontWeight: 'bold',
                  border: '2px solid #d9d9d9'
                }}
                src={profileData?.logo ? `data:image/jpeg;base64,${profileData.logo}` : undefined}
                icon={<BuildOutlined />}
              >
                {profileData?.companyName ? profileData.companyName.charAt(0).toUpperCase() : 'Ş'}
              </Avatar>
              <div>
                <Upload
                  name="logo"
                  listType="picture"
                  showUploadList={false}
                  beforeUpload={handleLogoUpload}
                  accept="image/*"
                >
                  <Button 
                    icon={<CameraOutlined />}
                    loading={logoMutation.isPending}
                  >
                    {logoMutation.isPending ? 'Yükleniyor...' : profileData?.logo ? 'Logoyu Değiştir' : 'Logo Yükle'}
                  </Button>
                </Upload>
                <div style={{ marginTop: '8px' }}>
                  <Text type="secondary" style={{ fontSize: '12px' }}>
                    JPG, PNG, GIF veya WEBP (Max. 10MB)
                  </Text>
                </div>
              </div>
            </div>
          </div>

          <Divider style={{ margin: '32px 0' }} />

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Şirket Adı"
                name="companyName"
                rules={[{ required: true, message: 'Şirket adı gereklidir' }]}
              >
                <Input disabled={!editing} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Vergi Numarası"
                name="taxNumber"
                rules={[{ required: true, message: 'Vergi numarası gereklidir' }]}
              >
                <Input disabled={!editing} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Yetkili Kişi"
                name="authorizedPerson"
                rules={[{ required: true, message: 'Yetkili kişi gereklidir' }]}
              >
                <Input disabled={!editing} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Telefon"
                name="phoneNumber"
                rules={[{ required: true, message: 'Telefon numarası gereklidir' }]}
              >
                <Input disabled={!editing} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Giriş E-postası"
                name="email"
              >
                <Input disabled />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Website"
                name="website"
              >
                <Input disabled={!editing} placeholder="https://example.com" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={24}>
            <Col span={12}>
              <Form.Item
                label="Şirket Büyüklüğü"
                name="companySize"
              >
                <Select 
                  disabled={!editing} 
                  options={companySizeOptions}
                  placeholder="Seçiniz"
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Sektör"
                name="industry"
              >
                <Input 
                  disabled={!editing} 
                  placeholder="Sektör adını giriniz"
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Adres"
            name="address"
          >
            <TextArea 
              disabled={!editing} 
              rows={3} 
              placeholder="Şirket adresi"
            />
          </Form.Item>


          {editing && (
            <Form.Item>
              <Space>
                <Button 
                  type="primary" 
                  htmlType="submit" 
                  loading={updateMutation.isPending}
                  icon={<SaveOutlined />}
                >
                  Kaydet
                </Button>
                <Button onClick={handleCancel}>
                  İptal
                </Button>
              </Space>
            </Form.Item>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default CompanyProfileTab;
